import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { GoogleOAuthProvider, GoogleLogin as GoogleLoginButton } from '@react-oauth/google';
import { register, googleLogin, sendOTP, verifyOTP, checkAvailability } from '../../services/auth';
import config from '../../config';

const Register = ({ onRegister, onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
  });
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    setMounted(true);
    document.body.classList.add('public-page');
    return () => {
      document.body.classList.remove('public-page');
    };
  }, []);

  // Cooldown timer for resend OTP
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSendOTP = async () => {
    setOtpLoading(true);
    setError('');
    
    // First check if username and email are available
    const availabilityResult = await checkAvailability(formData.username, formData.email);
    
    if (!availabilityResult.success) {
      setError(availabilityResult.error);
      setOtpLoading(false);
      return;
    }
    
    if (!availabilityResult.available) {
      setError(availabilityResult.message);
      setOtpLoading(false);
      return;
    }
    
    // If available, send OTP
    const result = await sendOTP(formData.email);
    
    if (result.success) {
      setResendCooldown(60); // 60 second cooldown
      setCurrentStep(2);
    } else {
      setError(result.error);
    }
    
    setOtpLoading(false);
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }
    
    setOtpLoading(true);
    setError('');
    
    const result = await verifyOTP(formData.email, otp);
    
    if (result.success) {
      setCurrentStep(3);
    } else {
      setError(result.error);
    }
    
    setOtpLoading(false);
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    
    setOtpLoading(true);
    setError('');
    
    const result = await sendOTP(formData.email);
    
    if (result.success) {
      setResendCooldown(60);
      setOtp('');
    } else {
      setError(result.error);
    }
    
    setOtpLoading(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    const result = await register({
      username: formData.username,
      email: formData.email,
      password: formData.password,
      full_name: formData.full_name || null,
    });

    if (result.success) {
      // Use replace to navigate and load fresh - this changes URL and loads the page
      window.location.replace('/prompts');
    } else {
      setError(result.error || 'Registration failed. Please try again.');
    }

    setLoading(false);
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError('');

    const result = await googleLogin(credentialResponse.credential);

    if (result.success) {
      // Use replace to navigate and load fresh - this changes URL and loads the page
      window.location.replace('/prompts');
    } else {
      setError(result.error || 'Google sign-up failed');
    }

    setLoading(false);
  };

  const handleGoogleError = () => {
    setError('Google sign-up was cancelled or failed');
  };

  const features = [
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
        </svg>
      ),
      text: 'Create AI features in plain English'
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
        </svg>
      ),
      text: 'Compare ChatGPT, Claude & Llama side-by-side'
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
        </svg>
      ),
      text: 'Never lose your work with version history'
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z" />
        </svg>
      ),
      text: 'Deploy via SDK when you\'re ready'
    },
  ];

  return (
    <div className="min-h-screen w-full flex bg-bg-primary">
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {/* Aurora gradient layers */}
        <div className="absolute inset-0 aurora-bg" />
        
        {/* Large floating orbs */}
        <div className="absolute rounded-full orb-1" />
        <div className="absolute rounded-full orb-2" />
        <div className="absolute rounded-full orb-3" />


        {/* Floating particles */}
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className={`absolute rounded-full particle particle-${(i % 3) + 1}`}
            style={{
              width: `${2 + (i % 3) * 2}px`,
              height: `${2 + (i % 3) * 2}px`,
              right: `${8 + (i * 5.5) % 85}%`,
              top: `${12 + (i * 5.5) % 75}%`,
              animationDelay: `${i * 0.2}s`,
            }}
          />
        ))}

        {/* Glowing accent lines */}
        <svg className="absolute inset-0 w-full h-full opacity-30">
          <defs>
            <linearGradient id="regLineGrad" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(50, 205, 50, 0)" />
              <stop offset="50%" stopColor="rgba(50, 205, 50, 0.35)" />
              <stop offset="100%" stopColor="rgba(50, 205, 50, 0)" />
            </linearGradient>
          </defs>
          <line x1="100%" y1="35%" x2="40%" y2="100%" stroke="url(#regLineGrad)" strokeWidth="1" className="line-pulse" />
          <line x1="60%" y1="0%" x2="0%" y2="55%" stroke="url(#regLineGrad)" strokeWidth="0.5" className="line-pulse-reverse" />
        </svg>
      </div>

      {/* Navigation Header */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 px-6 lg:px-12 py-4 transition-all duration-600 ease-out ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-5'
        }`}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <img 
              src="/logo.png"
              alt="ForgeVault Logo" 
              className="w-9 h-9 object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <span className="text-lg font-bold transition-colors duration-300 group-hover:text-primary-400 text-text-primary">ForgeVault</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium transition-colors duration-300 text-primary-300/70 hover:text-primary-500">
              Home
            </Link>
            <Link to="/features" className="text-sm font-medium transition-colors duration-300 text-primary-300/70 hover:text-primary-500">
              Features
            </Link>
            <Link to="/docs" className="text-sm font-medium transition-colors duration-300 text-primary-300/70 hover:text-primary-500">
              Docs
            </Link>
            <Link to="/about" className="text-sm font-medium transition-colors duration-300 text-primary-300/70 hover:text-primary-500">
              About
            </Link>
            <Link to="/contact" className="text-sm font-medium transition-colors duration-300 text-primary-300/70 hover:text-primary-500">
              Contact
            </Link>
          </nav>
          
          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/login"
              className="px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 text-primary-300/80 hover:text-primary-500 hover:-translate-y-0.5"
            >
              Sign In
            </Link>
            <span className="px-5 py-2.5 text-sm font-semibold rounded-xl relative overflow-hidden bg-gradient-to-r from-primary-600 to-primary-500 text-black shadow-glow">
              Get Started
            </span>
          </div>
        </div>
      </header>

      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 relative z-10">
        <div 
          className={`w-full max-w-sm transition-all duration-800 ease-out ${
            mounted ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'
          }`}
          style={{ transitionDelay: '0.1s' }}
        >
          <div className="rounded-2xl p-8 relative overflow-hidden bg-bg-secondary/90 backdrop-blur-lg border border-primary-500/15 shadow-2xl">
            {/* Glow effect */}
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-primary-500/8 to-transparent" />
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-500/30 to-transparent" />

            {/* Mobile logo */}
            <div className="lg:hidden text-center mb-5">
              <img 
                src="/logo.png"
                alt="ForgeVault Logo" 
                className="w-14 h-14 mx-auto mb-3 object-contain"
              />
              <h2 className="text-lg font-bold text-text-primary">Join ForgeVault</h2>
            </div>

            <div className="relative z-10">
              {/* Progress */}
              <div className="flex items-center gap-2 mb-5">
                {[1, 2, 3].map((step) => (
                  <div
                    key={step}
                    className={`h-1.5 rounded-full flex-1 transition-all duration-500 relative overflow-hidden ${
                      currentStep >= step 
                        ? 'bg-gradient-to-r from-primary-600 to-primary-500 shadow-glow-sm' 
                        : 'bg-primary-500/15'
                    }`}
                  >
                    {currentStep >= step && <div className="absolute inset-0 shimmer" />}
                  </div>
                ))}
              </div>

              <h2 className="text-xl font-semibold mb-1 text-text-primary">
                {currentStep === 1 ? 'Create your account' : currentStep === 2 ? 'Verify your email' : 'Set your password'}
              </h2>
              <p className="text-sm mb-5 text-primary-200/50">
                {currentStep === 1 ? 'Start building better prompts' : currentStep === 2 ? 'Enter the code sent to your email' : 'Secure your account'}
              </p>

              <form onSubmit={handleSubmit} className="space-y-3">
                {/* Inline Error Message */}
                {error && (
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm bg-error/10 border border-error/30 text-red-400">
                    <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <span>{error}</span>
                  </div>
                )}

                {currentStep === 1 ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1.5 text-text-secondary/90">
                        Username
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-0 rounded-xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 pointer-events-none bg-primary-500/10 blur-lg" />
                        <input
                          type="text"
                          name="username"
                          value={formData.username}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none transition-all duration-300 relative z-10 bg-bg-input/80 border border-primary-500/15 text-text-primary focus:border-primary-500/50 focus:ring-4 focus:ring-primary-500/10 placeholder:text-primary-200/30"
                          placeholder="Choose a username"
                          required
                          autoFocus
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1.5 text-text-secondary/90">
                        Email
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-0 rounded-xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 pointer-events-none bg-primary-500/10 blur-lg" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none transition-all duration-300 relative z-10 bg-bg-input/80 border border-primary-500/15 text-text-primary focus:border-primary-500/50 focus:ring-4 focus:ring-primary-500/10 placeholder:text-primary-200/30"
                          placeholder="Enter your email"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium mb-1.5 text-text-secondary/90">
                        Full Name
                        <span className="text-xs px-1.5 py-0.5 rounded bg-primary-500/10 text-primary-200/50">
                          Optional
                        </span>
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-0 rounded-xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 pointer-events-none bg-primary-500/10 blur-lg" />
                        <input
                          type="text"
                          name="full_name"
                          value={formData.full_name}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none transition-all duration-300 relative z-10 bg-bg-input/80 border border-primary-500/15 text-text-primary focus:border-primary-500/50 focus:ring-4 focus:ring-primary-500/10 placeholder:text-primary-200/30"
                          placeholder="Your full name"
                        />
                      </div>
                    </div>

                    <button 
                      type="button"
                      onClick={() => {
                        if (formData.username && formData.email) {
                          setError('');
                          handleSendOTP();
                        } else {
                          setError('Please fill in required fields');
                        }
                      }}
                      disabled={otpLoading}
                      className="w-full mt-2 py-3 text-sm font-semibold rounded-xl transition-all duration-300 inline-flex items-center justify-center gap-2 group relative overflow-hidden bg-gradient-to-br from-primary-600 to-primary-500 text-black shadow-glow hover:-translate-y-0.5 hover:shadow-glow-lg disabled:opacity-50"
                    >
                      <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity shimmer" />
                      <span className="relative z-10 flex items-center gap-2">
                        {otpLoading ? (
                          <>
                            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                              <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                            </svg>
                            Sending Code...
                          </>
                        ) : (
                          <>
                            Continue
                            <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                            </svg>
                          </>
                        )}
                      </span>
                    </button>

                    {/* Google Sign-Up */}
                    {config.google.clientId && (
                      <>
                        <div className="flex items-center gap-3 my-4">
                          <div className="flex-1 h-px bg-primary-500/15" />
                          <span className="text-xs text-primary-200/50">or sign up with</span>
                          <div className="flex-1 h-px bg-primary-500/15" />
                        </div>

                        <div className="flex justify-center">
                          <GoogleOAuthProvider clientId={config.google.clientId}>
                            <GoogleLoginButton
                              onSuccess={handleGoogleSuccess}
                              onError={handleGoogleError}
                              theme="filled_black"
                              size="large"
                              width="100%"
                              text="signup_with"
                              shape="rectangular"
                            />
                          </GoogleOAuthProvider>
                        </div>
                      </>
                    )}
                  </>
                ) : currentStep === 2 ? (
                  <>
                    {/* OTP Verification Step */}
                    <div className="text-center mb-4">
                      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-primary-500/10 border border-primary-500/20">
                        <svg className="w-8 h-8 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                        </svg>
                      </div>
                      <p className="text-sm text-primary-200/60">
                        We sent a 6-digit code to<br />
                        <span className="text-primary-400 font-medium">{formData.email}</span>
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1.5 text-text-secondary/90">
                        Verification Code
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-0 rounded-xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 pointer-events-none bg-primary-500/10 blur-lg" />
                        <input
                          type="text"
                          value={otp}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setOtp(value);
                            if (error) setError('');
                          }}
                          className="w-full px-4 py-3 rounded-xl text-xl text-center tracking-[0.5em] font-bold focus:outline-none transition-all duration-300 relative z-10 bg-bg-input/80 border border-primary-500/15 text-text-primary focus:border-primary-500/50 focus:ring-4 focus:ring-primary-500/10 placeholder:text-primary-200/30 placeholder:tracking-normal placeholder:text-sm placeholder:font-normal"
                          placeholder="Enter code"
                          maxLength={6}
                          autoFocus
                        />
                      </div>
                    </div>

                    <button 
                      type="button"
                      onClick={handleVerifyOTP}
                      disabled={otpLoading || otp.length !== 6}
                      className="w-full mt-2 py-3 text-sm font-semibold rounded-xl transition-all duration-300 inline-flex items-center justify-center gap-2 group relative overflow-hidden bg-gradient-to-br from-primary-600 to-primary-500 text-black shadow-glow hover:-translate-y-0.5 hover:shadow-glow-lg disabled:opacity-50"
                    >
                      <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity shimmer" />
                      <span className="relative z-10 flex items-center gap-2">
                        {otpLoading ? (
                          <>
                            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                              <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                            </svg>
                            Verifying...
                          </>
                        ) : (
                          <>
                            Verify Email
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                            </svg>
                          </>
                        )}
                      </span>
                    </button>

                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-primary-500/10">
                      <button 
                        type="button"
                        onClick={() => {
                          setCurrentStep(1);
                          setOtp('');
                        }}
                        className="text-sm text-primary-200/60 hover:text-primary-400 transition-colors"
                      >
                        ← Change email
                      </button>
                      <button 
                        type="button"
                        onClick={handleResendOTP}
                        disabled={resendCooldown > 0 || otpLoading}
                        className="text-sm font-medium text-primary-400 hover:text-primary-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Password Step */}
                    <div className="flex items-center gap-2 mb-4 p-3 rounded-xl bg-primary-500/10 border border-primary-500/20">
                      <svg className="w-5 h-5 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm text-primary-400">Email verified: {formData.email}</span>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1.5 text-text-secondary/90">
                        Password
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-0 rounded-xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 pointer-events-none bg-primary-500/10 blur-lg" />
                        <input
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none transition-all duration-300 relative z-10 bg-bg-input/80 border border-primary-500/15 text-text-primary focus:border-primary-500/50 focus:ring-4 focus:ring-primary-500/10 placeholder:text-primary-200/30"
                          placeholder="Min. 6 characters"
                          required
                          autoFocus
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1.5 text-text-secondary/90">
                        Confirm Password
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-0 rounded-xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 pointer-events-none bg-primary-500/10 blur-lg" />
                        <input
                          type="password"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none transition-all duration-300 relative z-10 bg-bg-input/80 border border-primary-500/15 text-text-primary focus:border-primary-500/50 focus:ring-4 focus:ring-primary-500/10 placeholder:text-primary-200/30"
                          placeholder="Confirm your password"
                          required
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 mt-2">
                      <button 
                        type="button"
                        onClick={() => {
                          setCurrentStep(1);
                          setOtp('');
                        }}
                        className="flex-1 py-3 text-sm font-medium rounded-xl transition-all duration-300 inline-flex items-center justify-center gap-2 bg-primary-500/8 border border-primary-500/15 text-primary-200/80 hover:bg-primary-500/15 hover:-translate-y-0.5"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                        </svg>
                        Start Over
                      </button>
                      <button 
                        type="submit" 
                        className="flex-[1.5] py-3 text-sm font-semibold rounded-xl transition-all duration-300 inline-flex items-center justify-center gap-2 disabled:opacity-50 group relative overflow-hidden bg-gradient-to-br from-primary-600 to-primary-500 text-black shadow-glow hover:-translate-y-0.5 hover:shadow-glow-lg"
                        disabled={loading}
                      >
                        <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity shimmer" />
                        <span className="relative z-10 flex items-center gap-2">
                          {loading ? (
                            <>
                              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                                <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                              </svg>
                              Creating...
                            </>
                          ) : (
                            <>
                              Create Account
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                              </svg>
                            </>
                          )}
                        </span>
                      </button>
                    </div>
                  </>
                )}
              </form>

              <div className="mt-5 pt-4 text-center border-t border-primary-500/10">
                <p className="text-sm text-primary-200/50">
                  Already have an account?{' '}
                  <button 
                    type="button" 
                    onClick={onSwitchToLogin} 
                    className="font-semibold cursor-pointer p-0 transition-all duration-300 hover:underline bg-transparent border-none text-primary-500 hover:drop-shadow-[0_0_10px_rgba(50,205,50,0.5)]"
                  >
                    Sign in
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Features */}
      <div 
        className={`hidden lg:flex lg:w-1/2 relative items-center justify-center px-12 transition-all duration-1000 ease-out ${
          mounted ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
        }`}
        style={{ transitionDelay: '0.2s' }}
      >
        {/* Decorative shapes */}
        <div className="absolute w-32 h-32 rounded-3xl rotate-12 decorative-shape-1" />
        <div className="absolute w-24 h-24 rounded-2xl -rotate-12 decorative-shape-2" />

        <div className="relative z-10 max-w-md">
          {/* Logo */}
          <img 
            src="/logo.png"
            alt="ForgeVault Logo" 
            className={`w-20 h-20 object-contain mb-8 transition-all duration-800 ease-bounce ${
              mounted ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-50 rotate-[10deg]'
            }`}
            style={{ transitionDelay: '0.3s' }}
          />

          <h1 
            className={`text-4xl font-bold mb-4 tracking-tight text-text-primary transition-all duration-800 ease-out ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '0.4s' }}
          >
            Start building today
          </h1>
          
          <p 
            className={`text-lg mb-10 leading-relaxed text-primary-200/70 transition-all duration-800 ease-out ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '0.5s' }}
          >
            Join teams using ForgeVault to ship AI features faster. Free to start, no credit card required.
          </p>

          {/* Features */}
          <div className="space-y-4">
            {features.map((feature, idx) => (
              <div 
                key={idx}
                className={`flex items-center gap-4 group transition-all duration-600 ease-out ${
                  mounted ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
                }`}
                style={{ transitionDelay: `${0.6 + idx * 0.1}s` }}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:-rotate-3 bg-primary-500/10 border border-primary-500/15 text-primary-500 shadow-glow-sm">
                  {feature.icon}
                </div>
                <span className="transition-colors duration-300 text-text-primary/80 group-hover:text-primary-300">
                  {feature.text}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .aurora-bg {
          background: 
            radial-gradient(ellipse 100% 70% at 70% 25%, rgba(50, 205, 50, 0.16) 0%, transparent 50%),
            radial-gradient(ellipse 80% 60% at 30% 70%, rgba(34, 139, 34, 0.12) 0%, transparent 45%),
            radial-gradient(ellipse 60% 50% at 10% 40%, rgba(50, 205, 50, 0.08) 0%, transparent 40%);
          animation: auroraShift 14s ease-in-out infinite alternate;
        }
        .orb-1 {
          width: 650px;
          height: 650px;
          background: radial-gradient(circle, rgba(50, 205, 50, 0.18) 0%, rgba(50, 205, 50, 0.04) 40%, transparent 70%);
          top: -10%;
          right: -15%;
          filter: blur(55px);
          animation: floatOrb1 30s ease-in-out infinite;
        }
        .orb-2 {
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(34, 139, 34, 0.14) 0%, rgba(34, 139, 34, 0.03) 45%, transparent 70%);
          bottom: -8%;
          left: -12%;
          filter: blur(50px);
          animation: floatOrb2 34s ease-in-out infinite;
        }
        .orb-3 {
          width: 380px;
          height: 380px;
          background: radial-gradient(circle, rgba(50, 205, 50, 0.1) 0%, transparent 60%);
          top: 45%;
          left: 40%;
          filter: blur(40px);
          animation: floatOrb3 24s ease-in-out infinite;
        }
        .particle {
          background: rgba(50, 205, 50, 0.7);
          box-shadow: 0 0 8px rgba(50, 205, 50, 0.4);
        }
        .particle:nth-child(even) {
          background: rgba(152, 251, 152, 0.5);
        }
        .particle-1 { animation: floatParticle1 11s ease-in-out infinite; }
        .particle-2 { animation: floatParticle2 13s ease-in-out infinite; }
        .particle-3 { animation: floatParticle3 9s ease-in-out infinite; }
        .line-pulse { animation: linePulse 7s ease-in-out infinite; }
        .line-pulse-reverse { animation: linePulse 9s ease-in-out infinite reverse; }
        .decorative-shape-1 {
          background: linear-gradient(135deg, rgba(50, 205, 50, 0.2) 0%, rgba(34, 139, 34, 0.05) 100%);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(50, 205, 50, 0.15);
          top: 15%;
          right: 15%;
          animation: floatShape1 15s ease-in-out infinite;
        }
        .decorative-shape-2 {
          background: linear-gradient(135deg, rgba(34, 139, 34, 0.15) 0%, rgba(50, 205, 50, 0.05) 100%);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(34, 139, 34, 0.1);
          bottom: 20%;
          left: 10%;
          animation: floatShape2 18s ease-in-out infinite;
        }
        .shimmer {
          background: linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.25) 50%, transparent 70%);
          animation: shimmer 2s infinite;
        }
        @keyframes auroraShift {
          0% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.85; transform: scale(1.03); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes floatOrb1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(-55px, 35px) scale(1.08); }
          50% { transform: translate(-30px, 70px) scale(0.94); }
          75% { transform: translate(25px, 35px) scale(1.04); }
        }
        @keyframes floatOrb2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(45px, -45px) scale(1.1); }
          50% { transform: translate(75px, 20px) scale(0.93); }
          75% { transform: translate(30px, -25px) scale(1.06); }
        }
        @keyframes floatOrb3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-45px, 30px) scale(1.06); }
          66% { transform: translate(-20px, -35px) scale(0.96); }
        }
        @keyframes floatParticle1 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.5; }
          25% { transform: translate(-15px, -22px) scale(1.2); opacity: 0.9; }
          50% { transform: translate(-28px, -10px) scale(0.9); opacity: 0.6; }
          75% { transform: translate(-10px, 14px) scale(1.1); opacity: 0.8; }
        }
        @keyframes floatParticle2 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.4; }
          33% { transform: translate(20px, -28px) scale(1.25); opacity: 1; }
          66% { transform: translate(-22px, -14px) scale(0.85); opacity: 0.5; }
        }
        @keyframes floatParticle3 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.6; }
          50% { transform: translate(14px, 22px) scale(1.15); opacity: 1; }
        }
        @keyframes linePulse {
          0%, 100% { opacity: 0.15; }
          50% { opacity: 0.5; }
        }
        @keyframes floatShape1 {
          0%, 100% { transform: translate(0, 0) rotate(12deg); }
          50% { transform: translate(-15px, 20px) rotate(18deg); }
        }
        @keyframes floatShape2 {
          0%, 100% { transform: translate(0, 0) rotate(-12deg); }
          50% { transform: translate(20px, -15px) rotate(-6deg); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
};

Register.propTypes = {
  onRegister: PropTypes.func.isRequired,
  onSwitchToLogin: PropTypes.func.isRequired,
};

export default Register;
